/**
 * Central SDK state — the single typed source of truth for what the SDK
 * currently knows: resolved options, normalized config, consent engine,
 * identity, and page attribution params.
 *
 * Created once by `core/init.ts`. Mutations happen only through kernel
 * lifecycle steps (init, consent change, identity refresh); everything else
 * reads.
 */

import type { PoolWebsitePayload, SdkConfig, AdAccountConfig } from './config/types';
import type { ResolvedOptions } from './options';
import type { ResolvedIds } from './identity';
import type { UaFields } from './ua/parse';
import type { ConsentEngine, ConsentStatus } from './consent/engine';
import type { PrivacyApi } from './consent/port';
import type { PageParams } from './analytics/pageParams';

export interface SdkState {
  options: ResolvedOptions;
  config: SdkConfig;
  /**
   * Raw pool website payload from the init fetch (the pool module normalizes its
   * slice). `null` in sealed builds, which carry a normalized config instead.
   */
  websitePayload: PoolWebsitePayload | null;
  ua: UaFields;
  /** Attribution URL params, parsed once at init. */
  pageParams: PageParams;
  /**
   * Ad account matched from `sourceMedium` (init or page UTMs).
   * Drives ecom tagging and which account pixel is wired.
   */
  activeAdAccount: AdAccountConfig | null;
  engine: ConsentEngine;
  /** Swapped in by the privacy module; inert when that module is not compiled in. */
  privacy: PrivacyApi;
  identity: { current: ResolvedIds | null };
  contentFetch: { inFlight: number; lastValue: unknown };
  /**
   * Locale of the served content, reported as `lo=` on every event. Set from
   * the pool post payload (`post.locale`) once content arrives — the payload is
   * authoritative because the pool may serve a fallback locale. `null` until
   * then; `lo` falls back to `options.defaultLocale` (the locale the fetch
   * requests), never to the visitor's geo.
   */
  content: { locale: string | null };
}

let state: SdkState | null = null;

export function setSdkState(next: SdkState): void {
  state = next;
}

export function getSdkStateOrNull(): SdkState | null {
  return state;
}

export function getSdkState(): SdkState {
  if (!state) {
    throw new Error('[kueez-sdk] init() must complete before this call');
  }
  return state;
}

export function isSdkStateReady(): boolean {
  return state !== null;
}

/** Attribution/media-buying gate. Defaults to enabled before init resolves. */
export function isMediaBuyingEnabled(): boolean {
  return state?.options.mediaBuyingEnabled ?? true;
}

export function makeFallbackIds(): ResolvedIds {
  return {
    userId: '0000000000000000',
    sessionId: '0000000000000000',
    pageviewId: '0000000000000000',
  };
}

export function currentIdentity(): ResolvedIds {
  return state?.identity.current ?? makeFallbackIds();
}

export function currentConsent(): ConsentStatus {
  return state?.engine.getStatus() ?? 'unknown';
}

/** Test-only — drop the state (privacy manager teardown is the caller's job). */
export function _resetSdkStateForTests(): void {
  state = null;
}
